import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';
import type { RenderLibrary } from './types.js';

export interface DatasetBinding {
  key: string;
  datasetName: string;
  columns?: string[];
  params?: Record<string, unknown>;
  limit?: number;
}

// ─── HTML layout (Copilot-authored) ─────────────────────────────────────
export interface HtmlDashboardLayout {
  kind: 'html';
  title: string;
  html: string;
  css?: string;
  libraries?: RenderLibrary[];
  datasets: DatasetBinding[];
}

// ─── Widget layout (drag-and-drop) ──────────────────────────────────────
export type WidgetAggregation =
  | 'sum'
  | 'avg'
  | 'count'
  | 'count_distinct'
  | 'min'
  | 'max'
  | 'first'
  | 'last';

export type WidgetNumberFormat =
  | 'number'
  | 'integer'
  | 'decimal_1'
  | 'decimal_2'
  | 'percent'
  | 'percent_0'
  | 'currency'
  | 'compact'
  | 'bytes'
  | 'duration_ms';

export interface WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface WidgetBinding {
  datasetKey: string;
  limit?: number;
}

export interface WidgetDrill {
  drillTargetId: string;
  paramMap?: Record<string, string>;
  mode?: 'widget' | 'row';
}

export interface BaseWidget {
  id: string;
  title: string;
  position: WidgetPosition;
  binding?: WidgetBinding;
  drill?: WidgetDrill;
  /** CSAGE-583: on a standard board, shown only to customers whose settings turn it on. */
  optional?: boolean;
}

export interface KpiWidget extends BaseWidget {
  type: 'kpi';
  config: {
    valueField: string;
    valueAgg?: WidgetAggregation;
    /** CSAGE-520 — when set, the tile shows valueField / denominatorField. */
    denominatorField?: string;
    denominatorAgg?: WidgetAggregation;
    format?: WidgetNumberFormat;
    unit?: string;
    comparisonField?: string;
    comparisonAgg?: WidgetAggregation;
    comparisonLabel?: string;
    deltaDirection?: 'positive-up' | 'negative-up' | 'neutral';
  };
}

export interface ChartWidget extends BaseWidget {
  type: 'bar' | 'line' | 'area';
  config: {
    xField: string;
    yField: string;
    yAgg?: WidgetAggregation;
    seriesField?: string;
    orientation?: 'vertical' | 'horizontal';
    stacked?: boolean;
    showLegend?: boolean;
    showGrid?: boolean;
    yFormat?: WidgetNumberFormat;
  };
}

export interface PieWidget extends BaseWidget {
  type: 'pie' | 'donut';
  config: {
    categoryField: string;
    valueField: string;
    valueAgg?: WidgetAggregation;
    showLegend?: boolean;
    showLabels?: boolean;
    maxSlices?: number;
  };
}

export interface TableWidget extends BaseWidget {
  type: 'table';
  config: {
    columns: Array<{
      field: string;
      label: string;
      format?: WidgetNumberFormat;
      align?: 'left' | 'center' | 'right';
      widthPct?: number;
      optional?: boolean;
    }>;
    rowLimit?: number;
    zebra?: boolean;
    dense?: boolean;
  };
}

export interface TextWidget extends BaseWidget {
  type: 'text';
  config: {
    markdown: string;
    align?: 'left' | 'center' | 'right';
    size?: 'sm' | 'md' | 'lg';
  };
}

export type Widget = KpiWidget | ChartWidget | PieWidget | TableWidget | TextWidget;
export type WidgetType = Widget['type'];

export interface FilterControl {
  id: string;
  kind: 'dateRange' | 'enum' | 'text' | 'number';
  label: string;
  field: string;
  /** CSAGE-510 — dateRange only: other names for the same date on other cubes. */
  fields?: string[];
  defaultValue?: unknown;
  options?: Array<{ value: string; label: string }>;
  multi?: boolean;
  /** CSAGE-598 — number only: "from" / "to" inputs; the value is { min?, max? }. */
  range?: boolean;
  /** CSAGE-583: see BaseWidget.optional. */
  optional?: boolean;
}

export interface WidgetGridConfig {
  columns: number;
  rowHeight: number;
  gap: number;
}

export interface WidgetDashboardLayout {
  kind: 'widgets';
  title: string;
  css?: string;
  libraries?: RenderLibrary[];
  datasets: DatasetBinding[];
  grid: WidgetGridConfig;
  widgets: Widget[];
  filters: FilterControl[];
}

export type DashboardLayout = HtmlDashboardLayout | WidgetDashboardLayout;

export interface DashboardDefinition {
  schemaVersion: '1.0.0' | '1.1.0';
  layout: DashboardLayout;
}

export interface DashboardSummary {
  id: string;
  tenantId: string;
  folderId: string | null;
  name: string;
  description: string | null;
  /** CSAGE-577: author notes; the API sends it only to dashboard authors. Never shown to viewers. */
  internalNote?: string | null;
  /**
   * CSAGE-583: one board served read-only to every customer provisioned for
   * it. Absent for any other board.
   */
  standard?: true;
  displayMode: 'dashboard' | 'kiosk';
  refreshIntervalSec: number;
  layoutKind: 'html' | 'widgets';
  currentVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardVersion {
  id: string;
  dashboardId: string;
  tenantId: string;
  version: number;
  definition: DashboardDefinition;
  publishedAt: string;
}

export interface DashboardWithCurrent extends DashboardSummary {
  currentVersion: DashboardVersion | null;
  draft: DashboardDraft | null;
}

export interface DashboardDraft {
  definition: DashboardDefinition;
  updatedAt: string;
  updatedBy: string | null;
}

export interface KioskToken {
  id: string;
  dashboardId: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export function useDashboards(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery<DashboardSummary[]>({
    queryKey: ['dashboards'],
    queryFn: () => api.fetch('/dashboards'),
    enabled: options?.enabled ?? true,
  });
}

export function useDashboard(id: string | null, opts: { refetchOnMount?: 'always' } = {}) {
  const api = useApi();
  return useQuery<DashboardWithCurrent>({
    queryKey: ['dashboards', id],
    queryFn: () => api.fetch(`/dashboards/${id}`),
    enabled: !!id,
    ...(opts.refetchOnMount ? { refetchOnMount: opts.refetchOnMount } : {}),
  });
}

export function useCreateDashboard() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      displayMode: 'dashboard' | 'kiosk';
      refreshIntervalSec: number;
      layoutKind?: 'html' | 'widgets';
      definition: DashboardDefinition;
    }) =>
      api.fetch<DashboardWithCurrent>('/dashboards', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

export function usePublishDashboardVersion(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // Omit `definition` to publish the current draft (typical path).
    mutationFn: (input: { definition?: DashboardDefinition } = {}) =>
      api.fetch<DashboardWithCurrent>(`/dashboards/${id}/versions`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards'] });
      void qc.invalidateQueries({ queryKey: ['dashboards', id] });
    },
  });
}

/** CSAGE-496 — draft saves and discards for one dashboard share this key
 *  prefix: their requests run in order (`scope`), and DashboardDetail
 *  waits for none to be in flight before it opens a designer. */
export function dashboardDraftMutationKey(id: string | null): readonly unknown[] {
  return ['dashboards', id, 'draft'];
}

export function useSaveDashboardDraft(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: dashboardDraftMutationKey(id),
    // One draft save at a time per dashboard, so an older response can
    // never land after a newer one.
    scope: { id: `dashboard-draft-${id}` },
    mutationFn: (input: { definition: DashboardDefinition }) =>
      api.fetch<DashboardWithCurrent>(`/dashboards/${id}/draft`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    // CSAGE-496: a designer that remounts while this save is in flight
    // (tab switch) must open the draft being saved, not the previous one.
    // A failed save rolls the cache back (onError) — the server never
    // stored that draft.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['dashboards', id], exact: true });
      const previous = qc.getQueryData<DashboardWithCurrent>(['dashboards', id]);
      if (previous) {
        qc.setQueryData<DashboardWithCurrent>(['dashboards', id], {
          ...previous,
          draft: {
            definition: input.definition,
            updatedAt: new Date().toISOString(),
            updatedBy: previous.draft?.updatedBy ?? null,
          },
        });
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      // A queued save runs its onMutate before this one settles; restoring
      // over it would drop its optimistic draft. Leave that case to the
      // refetch below.
      const newerPending = qc.isMutating({ mutationKey: dashboardDraftMutationKey(id) }) > 1;
      if (ctx?.previous && !newerPending) qc.setQueryData(['dashboards', id], ctx.previous);
      void qc.invalidateQueries({ queryKey: ['dashboards', id], exact: true });
    },
    onSuccess: (res) => {
      qc.setQueryData(['dashboards', id], res);
      void qc.invalidateQueries({ queryKey: ['dashboards', id] });
    },
  });
}

export function useDiscardDashboardDraft(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // Same queue as draft saves: a discard never runs alongside a save.
    mutationKey: [...dashboardDraftMutationKey(id), 'discard'],
    scope: { id: `dashboard-draft-${id}` },
    mutationFn: () =>
      api.fetch<DashboardWithCurrent>(`/dashboards/${id}/draft/discard`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: (res) => {
      qc.setQueryData(['dashboards', id], res);
      void qc.invalidateQueries({ queryKey: ['dashboards', id] });
    },
  });
}

export function useUpdateDashboard(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      displayMode?: 'dashboard' | 'kiosk';
      refreshIntervalSec?: number;
      /** null = move to top level. undefined = leave unchanged. */
      folderId?: string | null;
    }) =>
      api.fetch<DashboardWithCurrent>(`/dashboards/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards'] });
      void qc.invalidateQueries({ queryKey: ['dashboards', id] });
    },
  });
}

/**
 * Variant for callers that don't have a fixed dashboard id at hook-call
 * time (folder tree actions iterate over the list). The id is passed
 * per-mutation instead of bound at hook construction.
 */
export function useUpdateDashboardById() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        name?: string;
        displayMode?: 'dashboard' | 'kiosk';
        refreshIntervalSec?: number;
        folderId?: string | null;
      };
    }) =>
      api.fetch<DashboardWithCurrent>(`/dashboards/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['dashboards'] });
      void qc.invalidateQueries({ queryKey: ['dashboards', vars.id] });
    },
  });
}

export function useDeleteDashboard() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fetch<void>(`/dashboards/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

// ─── Kiosk tokens ──────────────────────────────────────────────────

export function useKioskTokens(id: string | null) {
  const api = useApi();
  return useQuery<KioskToken[]>({
    queryKey: ['dashboards', id, 'kiosk-tokens'],
    queryFn: () => api.fetch(`/dashboards/${id}/kiosk-tokens`),
    enabled: !!id,
  });
}

export function useMintKioskToken(dashboardId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label?: string; expiresAt?: string }) =>
      api.fetch<{ token: string; id: string; expiresAt: string | null }>(
        `/dashboards/${dashboardId}/kiosk-tokens`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards', dashboardId, 'kiosk-tokens'] });
    },
  });
}

/**
 * CSAGE-396: short-lived embed grant for the consumer viewer.
 *
 * Separate from `useMintKioskToken` on purpose. That one creates a durable
 * SHARE link and needs DashboardAdmin; this one only lets the current
 * session render a dashboard it can already read, so it is available to any
 * DashboardViewer. Using the share mint for viewing is what made opening a
 * dashboard require the INSIGHTS_DELETE entitlement.
 *
 * No cache invalidation: embed grants are deliberately absent from the
 * kiosk-token list, so there is nothing for the share-link UI to refresh.
 */
export function useMintEmbedGrant(dashboardId: string | null, scopePath?: string | null) {
  const api = useApi();
  return useMutation({
    mutationFn: () =>
      api.fetch<{ token: string; id: string; expiresAt: string }>(
        `/dashboards/${dashboardId}/embed-grants`,
        {
          method: 'POST',
          body: '{}',
          // CSAGE-586: a grant carries the scope it was minted under, so an
          // internal admin at the org root views a standard board as one
          // customer sees it by minting under that customer's path.
          ...(scopePath ? { headers: { 'x-canopy-scope': scopePath } } : {}),
        },
      ),
  });
}

export function useRevokeKioskToken(dashboardId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api
        .fetchRaw(`/dashboards/${dashboardId}/kiosk-tokens/${tokenId}`, {
          method: 'DELETE',
        })
        .then((r) => {
          if (!r.ok && r.status !== 204) throw new Error(`Revoke failed: ${r.status}`);
          return true;
        }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboards', dashboardId, 'kiosk-tokens'] });
    },
  });
}

// ─── Board audience: "Manage access" (CSAGE-557/558) ────────────────────

export type AudiencePrincipalType = 'role' | 'user' | 'path';

export interface AudiencePrincipal {
  realm: string;
  type: AudiencePrincipalType;
  id: string;
  label: string | null;
  source: string;
  createdBy: string | null;
  createdAt: string;
}

export interface DashboardAudience {
  dashboardId: string;
  /** false: everyone in the customer with dashboard access; true: only `principals`. */
  restricted: boolean;
  principals: AudiencePrincipal[];
}

export interface AudienceRoleCandidate {
  id: string;
  name: string;
  path: string;
  userCount: number | null;
}

export interface AudienceUserCandidate {
  id: string;
  email: string | null;
  username: string | null;
  name: string | null;
  path: string;
}

export interface AudienceCandidates<T> {
  items: T[];
  /** More matched than were returned: narrow the search. */
  truncated: boolean;
}

const audienceKey = (id: string | null) => ['dashboards', id, 'audience'] as const;

/**
 * CSAGE-581: store the new audience, and refetch the board list, the board and
 * the folders, which drop a board (and a folder left empty) the change hid from
 * the caller. Exact board keys: the prefix would also refetch this audience and
 * its pickers.
 */
/** CSAGE-586: every customer's standard boards (`useStandardOverview`). */
const standardOverviewKey = ['dashboards', 'standard', 'provisions'] as const;

function afterAudienceChange(
  qc: ReturnType<typeof useQueryClient>,
  id: string | null,
  data: DashboardAudience,
): void {
  qc.setQueryData(audienceKey(id), data);
  void qc.invalidateQueries({ queryKey: ['dashboards'], exact: true });
  void qc.invalidateQueries({ queryKey: ['dashboards', id], exact: true });
  void qc.invalidateQueries({ queryKey: ['folders'] });
  void qc.invalidateQueries({ queryKey: standardOverviewKey });
}

export function useDashboardAudience(id: string | null) {
  const api = useApi();
  return useQuery<DashboardAudience>({
    queryKey: audienceKey(id),
    queryFn: () => api.fetch(`/dashboards/${id}/audience`),
    enabled: !!id,
  });
}

/**
 * Picker search. The API runs it with the caller's own Canopy token, so the
 * results are only roles/users they may see, in the board's customer, that
 * can view dashboards. An empty query lists the first matches.
 */
export function useAudienceCandidates<K extends 'roles' | 'users'>(
  id: string | null,
  kind: K,
  query: string,
) {
  const api = useApi();
  const q = query.trim();
  return useQuery<
    AudienceCandidates<K extends 'roles' ? AudienceRoleCandidate : AudienceUserCandidate>
  >({
    queryKey: [...audienceKey(id), 'candidates', kind, q],
    queryFn: () =>
      api.fetch(
        `/dashboards/${id}/audience/candidates/${kind}${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: !!id,
    staleTime: 60 * 1000,
    // A refusal (no permission to view roles/users) will not change on retry.
    retry: false,
  });
}

/**
 * CSAGE-581: the API refuses an audience change that would hide the board from
 * the caller (409 `dashboard.audience.would_lose_access`) until it is resent
 * with `confirmLoseAccess: true`.
 */
export function isLoseAccessRefusal(err: unknown): boolean {
  const e = err as { status?: number; body?: { code?: string } } | null;
  return e?.status === 409 && e.body?.code === 'dashboard.audience.would_lose_access';
}

/** Restrict the board to its listed roles and users, or open it to the whole customer. */
export function useSetAudienceRestricted(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { restricted: boolean; confirmLoseAccess?: boolean | undefined }) =>
      api.fetch<DashboardAudience>(`/dashboards/${id}/audience`, {
        method: 'PUT',
        body: JSON.stringify(v),
      }),
    onSuccess: (data) => afterAudienceChange(qc, id, data),
  });
}

export function useChangeAudiencePrincipal(id: string | null, change: 'add' | 'remove') {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: {
      type: AudiencePrincipalType;
      id: string;
      confirmLoseAccess?: boolean | undefined;
    }) =>
      api.fetch<DashboardAudience>(`/dashboards/${id}/audience/principals`, {
        method: change === 'add' ? 'POST' : 'DELETE',
        body: JSON.stringify({ type: p.type, id: p.id, confirmLoseAccess: p.confirmLoseAccess }),
      }),
    onSuccess: (data) => afterAudienceChange(qc, id, data),
  });
}

// ─── Standard dashboards (CSAGE-583) ────────────────────────────────────

export interface StandardStatus {
  dashboardId: string;
  standard: boolean;
  provisions: string[];
  customersWithSettings: string[];
}

export interface StandardValidation {
  errors: string[];
  warnings: string[];
}

/** One customer's view of a standard dashboard. */
export interface CustomerSettings {
  customer: string;
  title: string | null;
  description: string | null;
  hiddenWidgetIds: string[];
  hiddenFilterIds: string[];
  shownOptionalIds: string[];
  columnOverrides: Record<string, Record<string, { label?: string; hidden?: boolean }>>;
  filterDefaults: Record<string, unknown>;
  refreshIntervalSec: number | null;
  audienceRestricted: boolean;
  updatedBy: string | null;
  updatedAt: string;
}

export type CustomerSettingsInput = Pick<
  CustomerSettings,
  'hiddenWidgetIds' | 'hiddenFilterIds' | 'shownOptionalIds' | 'columnOverrides' | 'filterDefaults'
> & { title: string | null; description: string | null; refreshIntervalSec: number | null };

const standardKey = (id: string | null) => ['dashboards', id, 'standard'] as const;

/** CSAGE-586: the standard boards one customer is provisioned for. */
export interface StandardCustomerBoards {
  customer: string;
  /** The customer's org path: the scope that views a board as them. */
  scopePath: string;
  boards: Array<{ dashboardId: string; name: string; restricted: boolean; customized: boolean }>;
}

export interface StandardOverview {
  /** False while CANOPY_STANDARD_DASHBOARDS is off: no customer is served these. */
  served: boolean;
  customers: StandardCustomerBoards[];
}

/** Every customer's standard boards. Internal admins at the org root only. */
export function useStandardOverview(enabled: boolean) {
  const api = useApi();
  return useQuery<StandardOverview>({
    queryKey: standardOverviewKey,
    queryFn: () => api.fetch('/dashboards/standard/provisions'),
    enabled,
  });
}

export function useStandardStatus(id: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery<StandardStatus>({
    queryKey: standardKey(id),
    queryFn: () => api.fetch(`/dashboards/${id}/standard`),
    enabled: !!id && enabled,
  });
}

export function useStandardValidation(id: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery<StandardValidation>({
    queryKey: [...standardKey(id), 'validation'],
    queryFn: () => api.fetch(`/dashboards/${id}/standard/validation`),
    enabled: !!id && enabled,
  });
}

/** Mark the board standard or not; also refreshes the board list (its badge). */
export function useSetStandard(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (standard: boolean) =>
      api.fetch<StandardStatus>(`/dashboards/${id}/standard`, {
        method: 'PUT',
        body: JSON.stringify({ standard }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(standardKey(id), data);
      void qc.invalidateQueries({ queryKey: ['dashboards'], exact: true });
      void qc.invalidateQueries({ queryKey: ['dashboards', id], exact: true });
      void qc.invalidateQueries({ queryKey: standardOverviewKey });
    },
  });
}

export function useSetProvision(id: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { customer: string; provisioned: boolean }) =>
      api.fetch<StandardStatus>(`/dashboards/${id}/provisions/${encodeURIComponent(v.customer)}`, {
        method: v.provisioned ? 'PUT' : 'DELETE',
      }),
    onSuccess: (data) => {
      qc.setQueryData(standardKey(id), data);
      void qc.invalidateQueries({ queryKey: standardOverviewKey });
    },
  });
}

export function useCustomerSettings(id: string | null, customer: string | null) {
  const api = useApi();
  return useQuery<{ settings: CustomerSettings | null }>({
    queryKey: [...standardKey(id), 'settings', customer],
    queryFn: () =>
      api.fetch(`/dashboards/${id}/customer-settings/${encodeURIComponent(customer ?? '')}`),
    enabled: !!id && !!customer,
  });
}

export function usePutCustomerSettings(id: string | null, customer: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerSettingsInput) =>
      api.fetch<{ settings: CustomerSettings; warnings: string[] }>(
        `/dashboards/${id}/customer-settings/${encodeURIComponent(customer ?? '')}`,
        { method: 'PUT', body: JSON.stringify(input) },
      ),
    onSuccess: (data) => {
      qc.setQueryData([...standardKey(id), 'settings', customer], { settings: data.settings });
      void qc.invalidateQueries({ queryKey: standardKey(id), exact: true });
      void qc.invalidateQueries({ queryKey: standardOverviewKey });
    },
  });
}
